import React, { useState } from 'react';
import styles from './VelocityPie.module.scss';

interface VelocityPieProps {
  good: number | undefined;
  fast: number | undefined;
  slow: number | undefined;
  missed?: number | undefined;
  subtitle?: string;
}

const VelocityPie: React.FC<VelocityPieProps> = ({
  good,
  fast,
  slow,
  missed,
  subtitle,
}) => {
  const [hovered, setHovered] = useState<string | null>(null);

  // Calculate percentages and handle the 'missed' case
  if (good === undefined || fast === undefined || slow === undefined) {
    return null;
  }
  const missedValue = missed || 0;

  // Count non-zero sections
  const nonZeroSections = [good, fast, slow, missedValue].filter(
    (val) => val > 0,
  ).length;

  const maxPercentage = Math.max(good, fast, slow, missedValue);
  const borderColor =
    maxPercentage === good
      ? 'var(--color-success)'
      : maxPercentage === fast
        ? 'var(--color-warning)'
        : maxPercentage === slow
          ? 'var(--color-danger)'
          : 'var(--color-text-5)';

  const handleMouseEnter = (key: string) => {
    setHovered(key);
  };

  const handleMouseLeave = () => {
    setHovered(null);
  };

  return (
    <div className={styles.velocityPie} style={{ borderColor }}>
      <div className={styles.labelsContainer}>
        <span
          className={styles.label}
          style={{ width: `${good}%` }}
          onMouseEnter={() => handleMouseEnter('good')}
          onMouseLeave={handleMouseLeave}
        >
          {good > 5 && `${good}%`}
        </span>
        <span
          className={styles.label}
          style={{ width: `${fast}%` }}
          onMouseEnter={() => handleMouseEnter('fast')}
          onMouseLeave={handleMouseLeave}
        >
          {fast > 5 && `${fast}%`}
        </span>
        <span
          className={styles.label}
          style={{ width: `${slow}%` }}
          onMouseEnter={() => handleMouseEnter('slow')}
          onMouseLeave={handleMouseLeave}
        >
          {slow > 5 && `${slow}%`}
        </span>
        {missedValue > 0 && (
          <span
            className={styles.label}
            style={{ width: `${missedValue}%` }}
            onMouseEnter={() => handleMouseEnter('missed')}
            onMouseLeave={handleMouseLeave}
          >
            {missedValue > 5 && `${missedValue}%`}
          </span>
        )}
      </div>
      <div
        className={`${styles.barContainer} ${styles[`sections${nonZeroSections}`]}`}
      >
        {good > 0 && (
          <div
            className={`${styles.section} ${nonZeroSections === 1 ? styles.fullSection : ''}`}
            style={{ backgroundColor: 'var(--color-success)', flex: good }}
            onMouseEnter={() => handleMouseEnter('good')}
            onMouseLeave={handleMouseLeave}
          >
            {good <= 5 && good > 0 && (
              <div className={styles.smallPercentageTooltip}>Good {good}%</div>
            )}
            {hovered === 'good' && (
              <div className={styles.hoverTooltip}>Good</div>
            )}
          </div>
        )}
        {fast > 0 && (
          <div
            className={styles.section}
            style={{ backgroundColor: 'var(--color-warning)', flex: fast }}
            onMouseEnter={() => handleMouseEnter('fast')}
            onMouseLeave={handleMouseLeave}
          >
            {fast <= 5 && fast > 0 && (
              <div className={styles.smallPercentageTooltip}>
                Faster {fast}%
              </div>
            )}
            {hovered === 'fast' && (
              <div className={styles.hoverTooltip}>Too Fast</div>
            )}
          </div>
        )}
        {slow > 0 && (
          <div
            className={styles.section}
            style={{ backgroundColor: 'var(--color-danger)', flex: slow }}
            onMouseEnter={() => handleMouseEnter('slow')}
            onMouseLeave={handleMouseLeave}
          >
            {slow <= 5 && slow > 0 && (
              <div className={styles.smallPercentageTooltip}>
                Slower {slow}%
              </div>
            )}
            {hovered === 'slow' && (
              <div className={styles.hoverTooltip}>Too Slow</div>
            )}
          </div>
        )}
        {missedValue > 0 && (
          <div
            className={styles.section}
            style={{ backgroundColor: 'var(--color-text-5)', flex: missedValue }}
            onMouseEnter={() => handleMouseEnter('missed')}
            onMouseLeave={handleMouseLeave}
          >
            {missedValue <= 5 && (
              <div className={styles.smallPercentageTooltip}>
                Missed {missedValue}%
              </div>
            )}
            {hovered === 'missed' && (
              <div className={styles.hoverTooltip}>Missed</div>
            )}
          </div>
        )}
      </div>
      {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
    </div>
  );
};

export default VelocityPie;
